/**
 * Pure formatters used across the admin's views. No theme deps; safe
 * to test as plain functions.
 */

/**
 * Format a minor-unit currency amount + ISO currency code into a
 * locale-aware display string.
 *
 * `amount_minor` is in the currency's smallest unit (cents for USD,
 * yen for JPY which has no minor unit). Returns an em-dash when the
 * amount is null / undefined / NaN — staff see "—" not "$NaN".
 *
 * Uses Intl.NumberFormat with the supplied currency code; falls back
 * to the raw `amount_minor` + code if the code is unknown.
 */
export function formatMinorCurrency(
  amount_minor: number | null | undefined,
  currency: string,
): string {
  if (amount_minor === null || amount_minor === undefined || Number.isNaN(amount_minor)) {
    return '—';
  }
  const code = currency.toUpperCase();
  try {
    // For minor-unit→display, divide by 10^minorUnitDigits. Intl
    // doesn't expose that directly; for the currencies the admin
    // sees (USD/EUR/GBP = 2 digits, JPY = 0 digits) the standard
    // formatter's `minimumFractionDigits` does the right thing when
    // we hand it the major-unit value. We pre-divide by 100 for the
    // common case and let Intl's per-currency rules adjust.
    //
    // Caveat: a currency with 3 minor digits (e.g. BHD) would be
    // mis-scaled by this 100-divide. Admin doesn't see those today;
    // P3's real billing schema can refine.
    const major = amount_minor / 100;
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: code,
    }).format(major);
  } catch {
    return `${amount_minor} ${code}`;
  }
}

/**
 * Admin console fixed display timezone. The Sill staff team is
 * US-East-only; rendering timestamps in `America/New_York` (handles
 * EST/EDT automatically) keeps the console unambiguous for the people
 * actually reading it. The " ET" suffix on time-bearing displays
 * makes the zone explicit on-screen so a staff member never has to
 * guess.
 */
const ADMIN_DISPLAY_TZ = 'America/New_York';

/**
 * Format an ISO-8601 UTC timestamp as `YYYY-MM-DD HH:MM ET` for
 * audit-style display. The zone is Eastern (EST/EDT auto-resolved
 * by `Intl.DateTimeFormat`'s `timeZone: 'America/New_York'` option).
 * Returns the raw input if Date parsing fails.
 */
export function formatIsoCompact(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  // Intl is locale-aware; we want fixed `YYYY-MM-DD HH:MM` regardless
  // of the staff member's browser locale → pin to `en-CA` which
  // emits ISO-style YMD by default. The result `2026-06-23, 14:05`
  // is split on the comma and joined with a space to match the
  // pre-ET display shape and avoid the comma in mono-table cells.
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: ADMIN_DISPLAY_TZ,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(d);
    // en-CA renders as `YYYY-MM-DD, HH:MM` — strip the comma.
    return `${parts.replace(', ', ' ')} ET`;
  } catch {
    return iso;
  }
}

/**
 * Format an ISO-8601 UTC timestamp as Eastern Time (date-only,
 * `YYYY-MM-DD`). Used for the billing `updated_at` line where the
 * stored value is a full timestamp but the admin only wants the day
 * portion — pinning the zone makes "what day did this happen?" agree
 * across staff laptops. Returns the raw input on parse failure.
 */
export function formatIsoDateOnlyET(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: ADMIN_DISPLAY_TZ,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(d);
  } catch {
    return iso;
  }
}

/**
 * Format an ISO-8601 date-only string (`YYYY-MM-DD`) for display.
 * Empty string for null/undefined so HTML date inputs accept it.
 * No timezone — a `YYYY-MM-DD` value carries no time of day, so
 * converting it to ET would be a category error (the value already
 * IS a calendar date).
 */
export function formatDateForInput(iso: string | null | undefined): string {
  if (iso === null || iso === undefined) return '';
  // The admin API documents `renews_at` as `YYYY-MM-DD`. Passes
  // through unchanged.
  return iso;
}

/**
 * Format an integer count with locale grouping (e.g. `12,345`).
 * Returns an em-dash for null / undefined / NaN.
 */
export function formatCount(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return Math.round(value).toLocaleString('en-US');
}

/**
 * Format a fraction in [0, 1] as a percentage (e.g. `0.873` → `87.3%`).
 * Returns an em-dash for null/undefined — the admin metrics surface
 * uses null when a denominator is zero (e.g. approval rate on a
 * zero-mandate bucket), not when the rate is `0%`.
 */
export function formatPct(value: number | null | undefined, digits = 1): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return `${(value * 100).toFixed(digits)}%`;
}

/**
 * Format a duration in seconds as a human-readable string. Picks the
 * coarsest sensible unit (`s` / `m` / `h` / `d`). Returns an em-dash
 * for null/undefined — admin metrics uses null when no resolutions
 * landed in the window, not `0s`.
 */
export function formatDurationSeconds(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || Number.isNaN(seconds)) return '—';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = seconds / 60;
  if (minutes < 60) return `${minutes.toFixed(minutes < 10 ? 1 : 0)}m`;
  const hours = minutes / 60;
  if (hours < 48) return `${hours.toFixed(hours < 10 ? 1 : 0)}h`;
  const days = hours / 24;
  return `${days.toFixed(days < 10 ? 1 : 0)}d`;
}

/**
 * Format a UTC day-bucket start as a short x-axis tick ("May 26",
 * "Jun 5"; no year — the header states the window). The admin rollups
 * bucket by UTC day, so the label is rendered in UTC: rendering a UTC
 * midnight in New York time put every tick one day early after 20:00
 * ET (analytics audit 2026-11-02).
 */
export function formatBucketDateUTC(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  try {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: 'UTC',
      month: 'short',
      day: 'numeric',
    }).format(d);
  } catch {
    return iso;
  }
}

/**
 * Format a "last active" ISO-8601 UTC timestamp. Returns `"Never"`
 * when the value is null/undefined — the admin's `last_active_at` is
 * NULL when the account has never recorded real activity. Otherwise
 * defers to `formatIsoCompact` (ET, `YYYY-MM-DD HH:MM ET`).
 */
export function formatLastActive(iso: string | null | undefined): string {
  if (iso === null || iso === undefined) return 'Never';
  return formatIsoCompact(iso);
}

/**
 * Format a number of days for the engagement / cohort-tenure displays.
 * Returns `"—"` for null/undefined/NaN. Picks a friendly unit:
 *   - < 1   → "<1 day"
 *   - 1     → "1 day"
 *   - >=2   → "N days" (rounded to one decimal when < 10)
 */
export function formatDays(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  if (value < 1) {
    return value <= 0 ? '0 days' : '<1 day';
  }
  if (value < 2) return '1 day';
  const rounded = value < 10 ? value.toFixed(1) : Math.round(value).toString();
  return `${rounded} days`;
}

/**
 * Format a fractional number with a single decimal place; the metrics
 * engagement block uses this for `events_per_active_account`. Returns
 * `"—"` for null/undefined/NaN.
 */
export function formatDecimal(value: number | null | undefined, digits = 1): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return value.toLocaleString('en-US', {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  });
}

/**
 * Format an ISO-8601 UTC timestamp as a short, calm "X ago" / "in X"
 * relative phrase. Used by the Test Suite view to put a quick "5m
 * ago" alongside each CI run while the absolute ET stamp lives on the
 * row's machine-id line.
 *
 * Returns the raw input on parse failure; returns `'just now'` for
 * deltas under 30s in either direction.
 */
export function formatRelativeShort(iso: string, now: Date = new Date()): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const deltaSec = Math.round((now.getTime() - d.getTime()) / 1000);
  const abs = Math.abs(deltaSec);
  if (abs < 30) return 'just now';
  const past = deltaSec >= 0;
  let value: number;
  let unit: string;
  if (abs < 60) {
    value = abs;
    unit = 's';
  } else if (abs < 60 * 60) {
    value = Math.round(abs / 60);
    unit = 'm';
  } else if (abs < 60 * 60 * 24) {
    value = Math.round(abs / 3600);
    unit = 'h';
  } else if (abs < 60 * 60 * 24 * 30) {
    value = Math.round(abs / 86400);
    unit = 'd';
  } else if (abs < 60 * 60 * 24 * 365) {
    value = Math.round(abs / (86400 * 30));
    unit = 'mo';
  } else {
    value = Math.round(abs / (86400 * 365));
    unit = 'y';
  }
  return past ? `${value}${unit} ago` : `in ${value}${unit}`;
}

/**
 * Format a `YYYY-MM-01` cohort month string as `Mon YYYY` (e.g.
 * `Jun 2026`). Used as the cohort heatmap row label. The cohort key
 * IS a calendar month, not a timestamp — no timezone conversion.
 */
export function formatCohortMonth(yyyy_mm_01: string): string {
  const m = /^(\d{4})-(\d{2})-01$/.exec(yyyy_mm_01);
  if (m === null) return yyyy_mm_01;
  const year = Number(m[1]);
  const month = Number(m[2]) - 1;
  if (Number.isNaN(year) || Number.isNaN(month)) return yyyy_mm_01;
  const MONTHS = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
  ] as const;
  const label = MONTHS[month] ?? '';
  return `${label} ${year}`;
}
